import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { TeacherPaymentHistory } from './TeacherPaymentHistory';
import { PaymentForm } from './payments/PaymentForm';
import { PaymentSuccess } from './payments/PaymentSuccess';
import { PaymentSummary } from './payments/PaymentSummary';
import { format } from 'date-fns';
import { ArrowLeft, Printer } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getTeacherById } from '@/services/db/teachers';
import { getHeaderHtml, getFooterHtml, getCommonPrintStyles } from '@/utils/printing/printStyles';
import { downloadAsJPG, createPrintDocument } from '@/utils/printing/printingBridge';

enum PaymentStep {
  SUMMARY,
  PAYMENT_FORM,
  SUCCESS
}

export const TeacherPaymentProcessor = ({ 
  teacherId, 
  teacherName,
  salary,
  onPaymentComplete
}: { 
  teacherId: string; 
  teacherName: string;
  salary: number;
  onPaymentComplete?: () => void;
}) => {
  const [currentStep, setCurrentStep] = useState<PaymentStep>(PaymentStep.SUMMARY);
  const [paymentDetails, setPaymentDetails] = useState({
    amount: salary || 0,
    allowances: [] as Array<{id: string, description: string, amount: number, type: string}>,
    deductions: [] as Array<{id: string, description: string, amount: number, type: string}>,
    paymentMethod: 'bank_transfer',
    notes: '',
    paymentPeriod: format(new Date(), 'MMMM yyyy'),
    totalAllowances: 0,
    totalDeductions: 0
  });
  const [refreshHistory, setRefreshHistory] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid'>('pending');
  const [lastPaymentDate, setLastPaymentDate] = useState<string | null>(null);
  const [currentPaymentPeriod, setCurrentPaymentPeriod] = useState<string>(format(new Date(), 'MMMM yyyy'));
  
  useEffect(() => {
    // Check if a payment for the current month exists
    const paymentsKey = `teacher_payments_${teacherId}`;
    const existingPayments = JSON.parse(localStorage.getItem(paymentsKey) || '[]');
    
    const currentMonth = format(new Date(), 'MMMM yyyy');
    setCurrentPaymentPeriod(currentMonth);
    
    // Check if there's a payment for the current month
    const currentMonthPayment = existingPayments.find((p: any) => 
      p.paymentPeriod === currentMonth || 
      (p.date && format(new Date(p.date), 'MMMM yyyy') === currentMonth)
    );
    
    if (currentMonthPayment) {
      setPaymentStatus('paid');
      setLastPaymentDate(currentMonthPayment.date);
    } else {
      setPaymentStatus('pending');
      setLastPaymentDate(null);
    }
    
    // Get the most recent payment date
    if (existingPayments.length > 0) {
      const mostRecent = existingPayments.reduce((latest: any, payment: any) => {
        return new Date(payment.date) > new Date(latest.date) ? payment : latest;
      }, existingPayments[0]);
      
      if (mostRecent && mostRecent.date) {
        setLastPaymentDate(mostRecent.date);
      }
    }
  }, [teacherId, refreshHistory]);

  const handleProceedToPayment = () => {
    setCurrentStep(PaymentStep.PAYMENT_FORM);
  };

  const handlePaymentSubmit = (formData: any) => {
    // Process payment logic
    const totalAmount = calculateTotalAmount(formData);
    
    const paymentData = {
      teacherId,
      ...formData,
      date: new Date().toISOString(),
      paymentPeriod: formData.paymentPeriod || currentPaymentPeriod,
      status: 'completed',
      totalAmount
    };
    
    // Here you would typically save to database
    console.log('Processing teacher payment:', paymentData);
    
    // Save payment to localStorage for history tracking
    const paymentsKey = `teacher_payments_${teacherId}`;
    const existingPayments = JSON.parse(localStorage.getItem(paymentsKey) || '[]');
    const newPayment = {
      id: crypto.randomUUID(),
      date: new Date().toISOString(),
      amount: totalAmount,
      paymentPeriod: paymentData.paymentPeriod,
      status: 'completed',
      method: formData.paymentMethod,
      allowances: formData.allowances || [],
      deductions: formData.deductions || [],
      totalAllowances: formData.totalAllowances || 0,
      totalDeductions: formData.totalDeductions || 0,
      baseAmount: formData.amount
    };
    localStorage.setItem(paymentsKey, JSON.stringify([newPayment, ...existingPayments]));
    
    // Update payment status
    setPaymentStatus('paid');
    setLastPaymentDate(new Date().toISOString());
    
    // Force history refresh by updating the state
    setRefreshHistory(prev => prev + 1);
    
    // Show success message using sonner toast
    toast.success(`Payment of ${totalAmount.toFixed(2)} AFN processed for ${teacherName} (${paymentData.paymentPeriod})`);
    
    // Move to success screen
    setPaymentDetails({...formData, paymentPeriod: paymentData.paymentPeriod});
    setCurrentStep(PaymentStep.SUCCESS);
    
    // Notify parent component if needed
    if (onPaymentComplete) {
      setTimeout(() => {
        onPaymentComplete();
      }, 2000); // Give time for the user to see the success screen
    }
  };

  const calculateTotalAmount = (data: any) => {
    const baseAmount = Number(data.amount) || 0;
    const allowancesAmount = data.totalAllowances || 0;
    const deductionsAmount = data.totalDeductions || 0;
    return baseAmount + allowancesAmount - deductionsAmount;
  };

  const handlePrintPaymentReceipt = async () => {
    try {
      // Show loading toast
      const loadingToast = toast.loading('Generating payment receipt JPG...');
      
      // Get full teacher data to access profile image
      const teacherData = await getTeacherById(teacherId);
      
      if (!teacherData) {
        toast.error("Could not retrieve teacher data for receipt");
        return;
      }
      
      // Get the most recent payment for this teacher
      const paymentsKey = `teacher_payments_${teacherId}`;
      const existingPayments = JSON.parse(localStorage.getItem(paymentsKey) || '[]');
      
      if (existingPayments.length === 0) {
        toast.error("No payment history found");
        return;
      }
      
      const latestPayment = existingPayments[0]; // Most recent payment
      
      // Set up title and styles for individual receipt
      const title = 'Payment Receipt';
      const styles = `
        ${getCommonPrintStyles()}
        .receipt-content {
          margin-top: 15px;
        }
        .receipt-header {
          text-align: center;
          margin-bottom: 20px;
          padding: 15px;
          border: 2px solid #3D7069;
          background-color: #f0f7f5;
          border-radius: 8px;
        }
        .receipt-title {
          font-size: 1.4em;
          font-weight: bold;
          color: #3D7069;
          margin-bottom: 5px;
        }
        .teacher-info, .payment-details {
          margin-bottom: 20px;
          padding: 15px;
          border: 1px solid #ddd;
          background-color: #f9f9f9;
          border-radius: 5px;
        }
        .section-title {
          font-size: 1.1em;
          font-weight: bold;
          color: #3D7069;
          margin-bottom: 10px;
          border-bottom: 1px solid #ddd;
          padding-bottom: 5px;
        }
        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          padding: 5px 0;
        }
        .info-label {
          font-weight: bold;
          color: #555;
        }
        .info-value {
          color: #333;
        }
        .amount-highlight {
          font-size: 1.2em;
          font-weight: bold;
          color: #3D7069;
          text-align: center;
          padding: 10px;
          background-color: #e8f5e8;
          border-radius: 5px;
          margin: 15px 0;
        }
        .page-content {
          min-height: 800px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .footer {
          margin-top: auto;
          page-break-inside: avoid;
        }
      `;
      
      // Create receipt content
      const content = `
        <div class="page-content">
          ${getHeaderHtml(title)}
          
          <div class="receipt-content">
            <div class="receipt-header">
              <div class="receipt-title">SALARY PAYMENT RECEIPT</div>
              <div>Receipt #: ${latestPayment.id}</div>
            </div>
            
            <div class="teacher-info">
              <div class="section-title">Teacher Information</div>
              <div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value">${teacherName}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Teacher ID:</span>
                <span class="info-value">${teacherId}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Position:</span>
                <span class="info-value">${teacherData.position || 'Not specified'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Department:</span>
                <span class="info-value">${teacherData.department || 'Not specified'}</span>
              </div>
            </div>
            
            <div class="payment-details">
              <div class="section-title">Payment Details</div>
              <div class="info-row">
                <span class="info-label">Payment Period:</span>
                <span class="info-value">${latestPayment.paymentPeriod || format(new Date(latestPayment.date), 'MMMM yyyy')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Date:</span>
                <span class="info-value">${format(new Date(latestPayment.date), 'PPP')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Method:</span>
                <span class="info-value">${latestPayment.method || 'Cash'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Base Amount:</span>
                <span class="info-value">${latestPayment.baseAmount || latestPayment.amount} AFN</span>
              </div>
              ${latestPayment.allowances && latestPayment.allowances.length > 0 ? `
                <div class="info-row">
                  <span class="info-label">Allowances:</span>
                  <span class="info-value">${latestPayment.totalAllowances || 0} AFN</span>
                </div>
              ` : ''}
              ${latestPayment.deductions && latestPayment.deductions.length > 0 ? `
                <div class="info-row">
                  <span class="info-label">Deductions:</span>
                  <span class="info-value">${latestPayment.totalDeductions || 0} AFN</span>
                </div>
              ` : ''}
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span class="info-value">${latestPayment.status}</span>
              </div>
            </div>
            
            <div class="amount-highlight">
              Total Amount Paid: ${latestPayment.amount} AFN
            </div>
          </div>
          
          ${getFooterHtml('teacher')}
        </div>
      `;
      
      // Generate the complete document
      const printDocument = createPrintDocument(content, `Payment_Receipt_${teacherName}_${latestPayment.id}`, styles);
      
      // Download as JPG directly - no window opening
      await downloadAsJPG(printDocument, `Payment_Receipt_${teacherName.replace(/\s+/g, '_')}_${format(new Date(latestPayment.date), 'yyyy-MM-dd')}`, {
        onError: (error) => {
          console.error('JPG download error:', error);
          toast.error('Failed to download payment receipt');
        }
      });
      
      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success('Payment receipt downloaded as JPG');
      
    } catch (error) {
      console.error("Error downloading payment receipt:", error);
      toast.error("Failed to download payment receipt");
    }
  };

  const handleReset = () => {
    setCurrentStep(PaymentStep.SUMMARY);
    setPaymentDetails({
      amount: salary || 0,
      allowances: [],
      deductions: [],
      paymentMethod: 'bank_transfer',
      notes: '',
      paymentPeriod: currentPaymentPeriod,
      totalAllowances: 0,
      totalDeductions: 0
    });
  };

  const renderContent = () => {
    switch (currentStep) {
      case PaymentStep.SUMMARY:
        return (
          <>
            <CardHeader>
              <CardTitle>Teacher Salary Payment</CardTitle>
              <CardDescription>
                Process salary payment for {teacherName}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <PaymentSummary 
                teacherName={teacherName}
                baseSalary={salary}
                paymentStatus={paymentStatus}
                lastPaymentDate={lastPaymentDate}
                currentPaymentPeriod={currentPaymentPeriod}
              />
              
              {paymentStatus === 'pending' ? (
                <Button 
                  className="w-full" 
                  onClick={handleProceedToPayment}
                >
                  Process Payment for {currentPaymentPeriod}
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button 
                    className="w-full" 
                    variant="outline"
                    disabled
                  >
                    Already Paid for {currentPaymentPeriod}
                  </Button>
                  <Button
                    className="w-full"
                    variant="secondary"
                    onClick={handlePrintPaymentReceipt}
                  >
                    <Printer className="mr-2 h-4 w-4" /> Print Latest Payment Receipt
                  </Button>
                </div>
              )}
              
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-2">Payment History</h3>
                <TeacherPaymentHistory teacherId={teacherId} key={`history-${refreshHistory}`} />
              </div>
            </CardContent>
          </>
        );
      
      case PaymentStep.PAYMENT_FORM:
        return (
          <>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
              <CardDescription>
                Complete the payment information for {teacherName} ({currentPaymentPeriod})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PaymentForm
                initialData={{...paymentDetails, paymentPeriod: currentPaymentPeriod}}
                onSubmit={handlePaymentSubmit}
                onCancel={handleReset}
              />
            </CardContent>
          </>
        );
      
      case PaymentStep.SUCCESS:
        return (
          <CardContent>
            <PaymentSuccess
              teacherName={teacherName}
              amount={calculateTotalAmount(paymentDetails)}
              paymentPeriod={paymentDetails.paymentPeriod}
              onClose={handleReset}
              onPrint={handlePrintPaymentReceipt}
            />
          </CardContent>
        );
    }
  };

  return (
    <Card className="w-full max-w-3xl mx-auto">
      {renderContent()}
    </Card>
  );
};
